/**
 * AccessGate — full-screen passcode prompt.
 *
 * Renders nothing if the worker is open (gated=false). Otherwise blocks
 * the app until the user enters a passcode that the worker accepts.
 *
 * Listens for `agent-auth-required` events (dispatched when any API call
 * gets a 401) so the gate re-appears if the passcode is rotated.
 */

import { Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { checkAuth, setPasscode } from "../lib/agent-api";

type Status = "checking" | "open" | "needs-passcode" | "verifying" | "error";

export function AccessGate({ children }: { children: React.ReactNode }) {
	const [status, setStatus] = useState<Status>("checking");
	const [input, setInput] = useState("");
	const [errorText, setErrorText] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const refresh = async () => {
		try {
			const { ok, gated } = await checkAuth();
			if (!gated) {
				setStatus("open");
				return;
			}
			setStatus(ok ? "open" : "needs-passcode");
		} catch {
			setStatus("error");
			setErrorText("Cannot reach the agent. Check your connection.");
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: refresh is stable
	useEffect(() => {
		refresh();
		const onAuth = () => setStatus("needs-passcode");
		window.addEventListener("agent-auth-required", onAuth);
		return () => window.removeEventListener("agent-auth-required", onAuth);
	}, []);

	useEffect(() => {
		if (status === "needs-passcode") inputRef.current?.focus();
	}, [status]);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!input.trim()) return;
		setStatus("verifying");
		setErrorText("");
		const trimmed = input.trim();
		const { ok } = await checkAuth(trimmed);
		if (ok) {
			setPasscode(trimmed);
			setStatus("open");
			setInput("");
		} else {
			setStatus("needs-passcode");
			setErrorText("Incorrect passcode.");
		}
	};

	if (status === "open") return <>{children}</>;

	return (
		<div className="min-h-screen flex items-center justify-center bg-background p-6">
			<div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-lg">
				<div className="flex flex-col items-center text-center mb-6">
					<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
						<Lock className="w-5 h-5 text-primary" />
					</div>
					<h1 className="text-lg font-semibold">Boring Agent</h1>
					<p className="text-sm text-muted-foreground mt-1">Enter the passcode to continue.</p>
				</div>
				<form onSubmit={submit} className="space-y-3">
					<input
						ref={inputRef}
						type="password"
						autoComplete="current-password"
						value={input}
						onChange={(e) => setInput(e.target.value)}
						placeholder="Passcode"
						className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
						disabled={status === "verifying" || status === "checking"}
					/>
					{errorText && <p className="text-xs text-red-500">{errorText}</p>}
					<button
						type="submit"
						className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
						disabled={status === "verifying" || status === "checking" || !input.trim()}
					>
						{status === "checking" && "Checking…"}
						{status === "verifying" && "Verifying…"}
						{(status === "needs-passcode" || status === "error") && "Continue"}
					</button>
				</form>
				<p className="text-[11px] text-muted-foreground text-center mt-6">
					Set by your operator. Contact them if you don't have one.
				</p>
			</div>
		</div>
	);
}
